import React, { Suspense, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { AlertTriangle, ArrowLeft, ExternalLink, Eye, FileCode, PenSquare, Save, X } from 'lucide-react';
import { toast } from 'react-hot-toast';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { blogAPI, blogApiError, type BlogArticle, type BlogArticleStatus, type BlogArticleUpdate, type BlogHub } from '../services/api';
import BlockInserter from '../components/BlockInserter';
import { articleSiteUrl } from '../constants/site';
import { isoToMskInput, mskInputToIso } from '../utils/msk';

// TipTap + формы блоков — тяжёлый кусок, грузим только на странице редактора.
const VisualEditor = React.lazy(() => import('../components/blog-editor/VisualEditor'));

type EditorTab = 'visual' | 'markdown' | 'preview';

interface FormState {
  slug: string;
  title: string;
  description: string;
  body: string;
  seoTitle: string;
  seoDescription: string;
  authorName: string;
  /** datetime-local в МСК */
  publishedAt: string;
  /** datetime-local в МСК */
  scheduledPublishAt: string;
  status: BlogArticleStatus;
  /** через запятую */
  tags: string;
  hubSlug: string;
  readingTimeMinutes: number;
}

const EMPTY_FORM: FormState = {
  slug: '',
  title: '',
  description: '',
  body: '',
  seoTitle: '',
  seoDescription: '',
  authorName: '',
  publishedAt: '',
  scheduledPublishAt: '',
  status: 'draft',
  tags: '',
  hubSlug: '',
  readingTimeMinutes: 0,
};

const STATUS_OPTIONS: Array<{ value: BlogArticleStatus; label: string }> = [
  { value: 'draft', label: 'Черновик' },
  { value: 'scheduled', label: 'Запланирована' },
  { value: 'published', label: 'Опубликована' },
];

function fromArticle(a: BlogArticle): FormState {
  return {
    slug: a.slug ?? '',
    title: a.title ?? '',
    description: a.description ?? '',
    body: a.body ?? '',
    seoTitle: a.seoTitle ?? '',
    seoDescription: a.seoDescription ?? '',
    authorName: a.authorName ?? '',
    publishedAt: isoToMskInput(a.publishedAt),
    scheduledPublishAt: isoToMskInput(a.scheduledPublishAt),
    status: a.status ?? 'draft',
    tags: (a.tags ?? []).join(', '),
    hubSlug: a.hubSlug ?? '',
    readingTimeMinutes: a.readingTimeMinutes ?? 0,
  };
}

function parseTags(s: string): string[] {
  const seen = new Set<string>();
  return s
    .split(/[,\n]/)
    .map((t) => t.trim())
    .filter((t) => t && !seen.has(t) && (seen.add(t), true));
}

const orNull = (s: string): string | null => (s.trim() ? s.trim() : null);

/** Полный payload формы в формате API (время — ISO UTC). */
function toPayload(f: FormState): Required<BlogArticleUpdate> {
  return {
    slug: f.slug.trim(),
    title: f.title.trim(),
    description: orNull(f.description),
    body: f.body,
    seoTitle: orNull(f.seoTitle),
    seoDescription: orNull(f.seoDescription),
    authorName: orNull(f.authorName),
    publishedAt: mskInputToIso(f.publishedAt),
    scheduledPublishAt: mskInputToIso(f.scheduledPublishAt),
    status: f.status,
    tags: parseTags(f.tags),
    hubSlug: orNull(f.hubSlug),
    readingTimeMinutes: Math.max(0, Math.floor(f.readingTimeMinutes || 0)),
  };
}

/** PUT — оверлей: шлём только изменённые поля, чтобы не перетирать чужое. */
function diffPayload(next: Required<BlogArticleUpdate>, base: Required<BlogArticleUpdate>): BlogArticleUpdate {
  const out: Record<string, unknown> = {};
  (Object.keys(next) as Array<keyof BlogArticleUpdate>).forEach((k) => {
    if (JSON.stringify(next[k]) !== JSON.stringify(base[k])) out[k] = next[k];
  });
  return out as BlogArticleUpdate;
}

const cyrillicToLatin: Record<string, string> = {
  а: 'a', б: 'b', в: 'v', г: 'g', д: 'd', е: 'e', ё: 'e', ж: 'zh', з: 'z',
  и: 'i', й: 'y', к: 'k', л: 'l', м: 'm', н: 'n', о: 'o', п: 'p', р: 'r',
  с: 's', т: 't', у: 'u', ф: 'f', х: 'kh', ц: 'ts', ч: 'ch', ш: 'sh', щ: 'shch',
  ъ: '', ы: 'y', ь: '', э: 'e', ю: 'yu', я: 'ya'
};

const inputCls = 'w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-sm';
const labelCls = 'block text-sm font-medium text-gray-700 mb-1';

const BlogArticleEditPage: React.FC = () => {
  const { id } = useParams<{ id?: string }>();
  const navigate = useNavigate();
  const isNew = !id || id === 'new';
  const numId = isNew ? NaN : parseInt(id!, 10);

  const [loading, setLoading] = useState(!isNew);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const [activeTab, setActiveTab] = useState<EditorTab>('visual');
  const [metaOpen, setMetaOpen] = useState(true);
  const [form, setForm] = useState<FormState>(EMPTY_FORM);
  const [hubs, setHubs] = useState<BlogHub[]>([]);
  const [snapshot, setSnapshot] = useState<Required<BlogArticleUpdate>>(() => toPayload(EMPTY_FORM));
  const [serverError, setServerError] = useState<{ message: string; problems: string[] } | null>(null);
  const [triedSave, setTriedSave] = useState(false);
  const textareaRef = useRef<HTMLTextAreaElement>(null);

  const load = useCallback(() => {
    if (isNew) return;
    if (Number.isNaN(numId)) {
      setLoadError('Некорректный ID статьи');
      setLoading(false);
      return;
    }
    setLoading(true);
    setLoadError(null);
    blogAPI
      .get(numId)
      .then(({ article }) => {
        const f = fromArticle(article);
        setForm(f);
        setSnapshot(toPayload(f));
      })
      .catch((e: unknown) => setLoadError(blogApiError(e, 'Ошибка загрузки статьи').message))
      .finally(() => setLoading(false));
  }, [isNew, numId]);

  useEffect(load, [load]);

  // Реестр рубрик — из blog-go; при ошибке поле остаётся свободным текстом без подсказок.
  useEffect(() => {
    let alive = true;
    blogAPI
      .categories()
      .then(({ categories }) => alive && setHubs(categories ?? []))
      .catch(() => undefined);
    return () => {
      alive = false;
    };
  }, []);

  const update = useCallback((patch: Partial<FormState>) => setForm((prev) => ({ ...prev, ...patch })), []);
  const onBodyChange = useCallback((body: string) => setForm((prev) => ({ ...prev, body })), []);

  const payload = useMemo(() => toPayload(form), [form]);
  const changes = useMemo(() => diffPayload(payload, snapshot), [payload, snapshot]);
  const dirty = Object.keys(changes).length > 0;

  // Закрытие вкладки/перезагрузка с несохранёнными правками. Переходы внутри SPA (BrowserRouter,
  // без data-router — useBlocker недоступен) ловим на ссылках этой страницы.
  useEffect(() => {
    if (!dirty) return;
    const onBeforeUnload = (e: BeforeUnloadEvent) => {
      e.preventDefault();
      e.returnValue = '';
    };
    window.addEventListener('beforeunload', onBeforeUnload);
    return () => window.removeEventListener('beforeunload', onBeforeUnload);
  }, [dirty]);

  const confirmLeave = (e: React.MouseEvent) => {
    if (dirty && !window.confirm('Есть несохранённые изменения. Уйти без сохранения?')) e.preventDefault();
  };

  const validation = useMemo(() => {
    const errs: Partial<Record<keyof FormState, string>> = {};
    if (!form.title.trim()) errs.title = 'Нужен заголовок';
    if (!form.slug.trim()) errs.slug = 'Нужен slug';
    // Шаблон проверяем только у нового/изменённого slug: старые статьи сохраняются как есть.
    else if (form.slug.trim() !== snapshot.slug && !/^[a-z0-9]+(?:-[a-z0-9]+)*$/.test(form.slug.trim())) errs.slug = 'Только латиница в нижнем регистре, цифры и дефисы';
    if (!form.body.trim()) errs.body = 'Тело статьи пустое';
    if (form.status === 'scheduled' && !mskInputToIso(form.scheduledPublishAt)) errs.scheduledPublishAt = 'Для запланированной статьи нужно время публикации';
    return errs;
  }, [form, snapshot.slug]);

  const save = async () => {
    setTriedSave(true);
    const first = Object.values(validation)[0];
    if (first) {
      toast.error(first);
      return;
    }
    if (!isNew && !dirty) {
      toast('Изменений нет');
      return;
    }
    setSaving(true);
    setServerError(null);
    try {
      if (isNew) {
        const { article } = await blogAPI.create(payload);
        setSnapshot(payload);
        toast.success('Статья создана');
        navigate(`/blog/${article.id}`, { replace: true });
      } else {
        await blogAPI.update(numId, changes);
        setSnapshot(payload);
        toast.success('Статья сохранена');
      }
    } catch (e) {
      const err = blogApiError(e, 'Ошибка сохранения');
      setServerError(err);
      toast.error(err.problems.length ? `${err.message} (${err.problems.length})` : err.message);
    } finally {
      setSaving(false);
    }
  };

  const saveRef = useRef(save);
  saveRef.current = save;
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === 's') {
        e.preventDefault();
        saveRef.current();
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, []);

  const generateSlug = () => {
    let s = form.title.toLowerCase();
    s = s.split('').map((c) => cyrillicToLatin[c] ?? c).join('');
    s = s.replace(/[^a-z0-9\s-]/g, '').replace(/\s+/g, '-').replace(/-+/g, '-').replace(/^-|-$/g, '');
    update({ slug: s });
  };

  const fieldError = (k: keyof FormState) => (triedSave ? validation[k] : undefined);
  const errCls = (k: keyof FormState) => (fieldError(k) ? ' border-red-500 ring-1 ring-red-300' : '');
  // Пока реестр рубрик не загружен, не ругаемся на незнакомую рубрику.
  const knownHub = !form.hubSlug || hubs.length === 0 || hubs.some((h) => h.slug === form.hubSlug.trim());

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12" role="status" aria-label="Загрузка статьи">
        <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-blue-600" />
      </div>
    );
  }

  if (loadError) {
    return (
      <div className="max-w-lg mx-auto py-12 text-center">
        <AlertTriangle className="h-10 w-10 text-red-500 mx-auto mb-3" aria-hidden />
        <p className="text-gray-900 font-medium mb-1">Не удалось открыть статью</p>
        <p className="text-gray-600 text-sm mb-4">{loadError}</p>
        <div className="flex justify-center gap-3">
          <button type="button" onClick={load} className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 text-sm font-medium">
            Повторить
          </button>
          <Link to="/blog" className="px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50 text-sm font-medium">
            К списку
          </Link>
        </div>
      </div>
    );
  }

  const siteUrl = !isNew && form.slug.trim() ? articleSiteUrl(form.slug, form.hubSlug) : null;
  const tabBtn = (tab: EditorTab, label: string, icon: React.ReactNode) => (
    <button
      type="button"
      role="tab"
      aria-selected={activeTab === tab}
      onClick={() => setActiveTab(tab)}
      className={`px-3 py-2 rounded-lg text-sm font-medium inline-flex items-center gap-1.5 ${
        activeTab === tab ? 'bg-blue-100 text-blue-700' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
      }`}
    >
      {icon}
      {label}
    </button>
  );

  return (
    <div>
      <div className="mb-4 flex flex-wrap items-center gap-x-4 gap-y-2">
        <Link to="/blog" onClick={confirmLeave} className="inline-flex items-center gap-2 text-gray-600 hover:text-gray-900">
          <ArrowLeft className="h-5 w-5" aria-hidden />
          К списку
        </Link>
        <h1 className="text-2xl font-bold text-gray-900 min-w-0 truncate">{isNew ? 'Новая статья' : form.title || 'Редактирование статьи'}</h1>
        {siteUrl && (
          <a
            href={siteUrl}
            target="_blank"
            rel="noopener noreferrer"
            className="ml-auto inline-flex items-center gap-1.5 px-3 py-1.5 text-sm text-blue-700 border border-blue-200 rounded-lg hover:bg-blue-50"
          >
            <ExternalLink className="h-4 w-4" aria-hidden />
            Открыть на сайте
          </a>
        )}
      </div>

      <form
        onSubmit={(e) => {
          e.preventDefault();
          save();
        }}
        className="space-y-4"
        noValidate
      >
        <section className="bg-white border border-gray-200 rounded-xl">
          <button
            type="button"
            className="w-full flex items-center justify-between px-4 py-3 text-left"
            aria-expanded={metaOpen}
            aria-controls="article-meta"
            onClick={() => setMetaOpen((v) => !v)}
          >
            <span className="font-semibold text-gray-900">Параметры публикации и SEO</span>
            <span className="text-sm text-gray-500">{metaOpen ? 'Свернуть' : 'Развернуть'}</span>
          </button>
          {metaOpen && (
            <div id="article-meta" className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4 px-4 pb-4">
              <div className="md:col-span-2 xl:col-span-2">
                <label className={labelCls} htmlFor="f-title">Заголовок (H1) *</label>
                <input id="f-title" type="text" value={form.title} onChange={(e) => update({ title: e.target.value })} className={inputCls + errCls('title')} placeholder="Заголовок статьи" />
                {fieldError('title') && <p className="text-xs text-red-600 mt-1">{fieldError('title')}</p>}
              </div>
              <div>
                <label className={labelCls} htmlFor="f-slug">Slug *</label>
                <div className="flex gap-2">
                  <input id="f-slug" type="text" value={form.slug} onChange={(e) => update({ slug: e.target.value })} className={`${inputCls} font-mono${errCls('slug')}`} placeholder="url-slug" />
                  <button type="button" onClick={generateSlug} className="px-3 py-2 bg-gray-100 rounded-lg hover:bg-gray-200 text-sm whitespace-nowrap">
                    Из заголовка
                  </button>
                </div>
                {fieldError('slug') && <p className="text-xs text-red-600 mt-1">{fieldError('slug')}</p>}
              </div>

              <div>
                <label className={labelCls} htmlFor="f-hub">Рубрика (профессия)</label>
                <input id="f-hub" type="text" list="blog-hubs" value={form.hubSlug} onChange={(e) => update({ hubSlug: e.target.value })} className={`${inputCls} font-mono`} placeholder="backend-razrabotchik" />
                <datalist id="blog-hubs">
                  {hubs.map((h) => (
                    <option key={h.slug} value={h.slug}>
                      {h.label}
                    </option>
                  ))}
                </datalist>
                <p className={`text-xs mt-1 ${knownHub ? 'text-gray-500' : 'text-amber-600'}`}>
                  {knownHub
                    ? hubs.find((h) => h.slug === form.hubSlug.trim())?.label || 'Без рубрики — адрес /blog/<slug>'
                    : 'Такой рубрики нет в реестре blog-go'}
                </p>
              </div>
              <div>
                <label className={labelCls} htmlFor="f-status">Статус</label>
                <select id="f-status" value={form.status} onChange={(e) => update({ status: e.target.value as BlogArticleStatus })} className={inputCls}>
                  {!STATUS_OPTIONS.some((o) => o.value === form.status) && <option value={form.status}>{form.status}</option>}
                  {STATUS_OPTIONS.map((o) => (
                    <option key={o.value} value={o.value}>
                      {o.label}
                    </option>
                  ))}
                </select>
              </div>
              <div>
                <label className={labelCls} htmlFor="f-scheduled">
                  Запланировать на (МСК){form.status === 'scheduled' ? ' *' : ''}
                </label>
                <input
                  id="f-scheduled"
                  type="datetime-local"
                  value={form.scheduledPublishAt}
                  onChange={(e) => update({ scheduledPublishAt: e.target.value })}
                  className={inputCls + errCls('scheduledPublishAt')}
                />
                {fieldError('scheduledPublishAt') ? (
                  <p className="text-xs text-red-600 mt-1">{fieldError('scheduledPublishAt')}</p>
                ) : (
                  <p className="text-xs text-gray-500 mt-1">Время по Москве; публикует расписание, если статус «Запланирована».</p>
                )}
              </div>

              <div>
                <label className={labelCls} htmlFor="f-published">Дата публикации (МСК)</label>
                <input id="f-published" type="datetime-local" value={form.publishedAt} onChange={(e) => update({ publishedAt: e.target.value })} className={inputCls} />
              </div>
              <div>
                <label className={labelCls} htmlFor="f-author">Автор</label>
                <input id="f-author" type="text" value={form.authorName} onChange={(e) => update({ authorName: e.target.value })} className={inputCls} placeholder="Имя автора" />
              </div>
              <div>
                <label className={labelCls} htmlFor="f-reading">Время чтения, мин</label>
                <input
                  id="f-reading"
                  type="number"
                  min={0}
                  value={form.readingTimeMinutes}
                  onChange={(e) => update({ readingTimeMinutes: parseInt(e.target.value, 10) || 0 })}
                  className={inputCls}
                />
                <p className="text-xs text-gray-500 mt-1">0 — посчитать автоматически по тексту.</p>
              </div>

              <div className="md:col-span-2 xl:col-span-3">
                <label className={labelCls} htmlFor="f-tags">Теги (через запятую)</label>
                <input id="f-tags" type="text" value={form.tags} onChange={(e) => update({ tags: e.target.value })} className={inputCls} placeholder="backend-razrabotchik, sobesedovanie" />
              </div>
              <div className="md:col-span-2 xl:col-span-3">
                <label className={labelCls} htmlFor="f-desc">Краткое описание</label>
                <textarea id="f-desc" value={form.description} onChange={(e) => update({ description: e.target.value })} rows={2} className={inputCls} placeholder="Для карточки в списке" />
              </div>
              <div className="md:col-span-1 xl:col-span-1">
                <label className={labelCls} htmlFor="f-seo-title">SEO: заголовок</label>
                <input id="f-seo-title" type="text" value={form.seoTitle} onChange={(e) => update({ seoTitle: e.target.value })} className={inputCls} placeholder="meta title" />
                <p className="text-xs text-gray-500 mt-1">{form.seoTitle.length} симв.</p>
              </div>
              <div className="md:col-span-1 xl:col-span-2">
                <label className={labelCls} htmlFor="f-seo-desc">SEO: описание</label>
                <textarea id="f-seo-desc" value={form.seoDescription} onChange={(e) => update({ seoDescription: e.target.value })} rows={2} className={inputCls} placeholder="meta description" />
                <p className="text-xs text-gray-500 mt-1">{form.seoDescription.length} симв.</p>
              </div>
            </div>
          )}
        </section>

        <section className="bg-white border border-gray-200 rounded-xl p-4">
          <div className="flex flex-wrap items-center gap-2 mb-3" role="tablist" aria-label="Режим редактора">
            {tabBtn('visual', 'Визуально', <PenSquare className="h-4 w-4" aria-hidden />)}
            {tabBtn('markdown', 'Markdown', <FileCode className="h-4 w-4" aria-hidden />)}
            {tabBtn('preview', 'Превью', <Eye className="h-4 w-4" aria-hidden />)}
            {fieldError('body') && <span className="text-xs text-red-600 ml-2">{fieldError('body')}</span>}
          </div>

          {activeTab === 'visual' && (
            <Suspense fallback={<div className="py-8 text-center text-sm text-gray-500">Загрузка визуального редактора…</div>}>
              <VisualEditor body={form.body} onChange={onBodyChange} />
            </Suspense>
          )}
          {activeTab === 'markdown' && (
            <>
              <BlockInserter textareaRef={textareaRef} value={form.body} onInsert={onBodyChange} />
              <textarea
                ref={textareaRef}
                value={form.body}
                onChange={(e) => onBodyChange(e.target.value)}
                rows={28}
                spellCheck={false}
                aria-label="Тело статьи в Markdown"
                className={`w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 font-mono text-sm${errCls('body')}`}
                placeholder="Текст статьи в Markdown..."
              />
              <p className="text-xs text-gray-500 mt-1">Аварийный режим: сырой markdown с фенсами ```rmblock. Правки видны во вкладке «Визуально».</p>
            </>
          )}
          {activeTab === 'preview' && (
            <div className="blog-preview border border-gray-300 rounded-lg p-4 bg-white min-h-[400px] text-gray-800 text-sm [&_h1]:text-2xl [&_h1]:font-bold [&_h2]:text-xl [&_h2]:font-bold [&_h2]:mt-4 [&_h3]:text-lg [&_h3]:font-semibold [&_ul]:list-disc [&_ul]:pl-6 [&_ol]:list-decimal [&_ol]:pl-6 [&_li]:my-0.5 [&_p]:my-2 [&_code]:bg-gray-100 [&_code]:px-1 [&_code]:rounded [&_code]:font-mono [&_pre]:bg-gray-100 [&_pre]:p-3 [&_pre]:rounded-lg [&_pre]:overflow-x-auto [&_blockquote]:border-l-4 [&_blockquote]:border-gray-300 [&_blockquote]:pl-4 [&_blockquote]:italic [&_blockquote]:text-gray-600 [&_hr]:my-4 [&_a]:text-blue-600 [&_a]:underline">
              <ReactMarkdown remarkPlugins={[remarkGfm]}>{form.body || '(пусто)'}</ReactMarkdown>
              <p className="text-gray-500 text-xs mt-2">Превью Markdown (рендер на сайте может отличаться: блоки guide.* здесь видны как код). Для вложенного пункта списка добавьте 2 пробела перед дефисом.</p>
            </div>
          )}
        </section>

        <div className="sticky bottom-0 z-20 -mx-4 sm:-mx-6 lg:-mx-8 px-4 sm:px-6 lg:px-8 py-3 bg-white/95 backdrop-blur border-t border-gray-200 shadow-[0_-4px_12px_rgba(0,0,0,0.04)]">
          {serverError && (
            <div className="mb-3 rounded-lg border border-red-200 bg-red-50 p-3 text-sm text-red-800" role="alert">
              <div className="flex items-start gap-2">
                <AlertTriangle className="h-4 w-4 mt-0.5 flex-none" aria-hidden />
                <div className="flex-1 min-w-0">
                  <p className="font-medium">{serverError.message}</p>
                  {serverError.problems.length > 0 && (
                    <ul className="mt-1 list-disc pl-5 space-y-0.5 max-h-40 overflow-y-auto">
                      {serverError.problems.map((p, i) => (
                        <li key={i}>{p}</li>
                      ))}
                    </ul>
                  )}
                </div>
                <button type="button" onClick={() => setServerError(null)} className="p-1 rounded hover:bg-red-100" aria-label="Скрыть ошибки">
                  <X className="h-4 w-4" aria-hidden />
                </button>
              </div>
            </div>
          )}
          <div className="flex flex-wrap items-center gap-3">
            <button
              type="submit"
              disabled={saving}
              className="inline-flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 font-medium disabled:opacity-50"
            >
              <Save className="h-5 w-5" aria-hidden />
              {saving ? 'Сохранение…' : isNew ? 'Создать' : 'Сохранить'}
            </button>
            <Link to="/blog" onClick={confirmLeave} className="px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50 font-medium">
              Отмена
            </Link>
            <span className={`text-sm ${dirty ? 'text-amber-700' : 'text-gray-500'}`} aria-live="polite">
              {dirty ? `Есть несохранённые изменения${isNew ? '' : ` (${Object.keys(changes).length})`}` : 'Все изменения сохранены'}
            </span>
            <span className="hidden sm:inline text-xs text-gray-400 ml-auto">⌘S / Ctrl+S — сохранить</span>
          </div>
        </div>
      </form>
    </div>
  );
};

export default BlogArticleEditPage;
